<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { ColumnDefinition } from '@/components/_global/BalTable/types';

import useBreakpoints from '@/composables/useBreakpoints';
import useDarkMode from '@/composables/useDarkMode';
import useFathom from '@/composables/useFathom';
import useNetwork, { networkConfig } from '@/composables/useNetwork';
import { Pool } from '@/services/pool/types';

import TokensWhite from '@/assets/images/icons/tokens_white.svg';
import TokensBlack from '@/assets/images/icons/tokens_black.svg';
import Jot from '@/assets/images/icons/jot.svg';
import Sot from '@/assets/images/icons/sot.svg';
import { PoolNote } from '@/types/untangled/pool';
import PoolsTableCapacityCell from './PoolsTableCapacityCell.vue';
import PoolsTableTargetReturnCell from './PoolsTableTargetReturnCell.vue';
import PoolsTableLiquidityCell from './PoolsTableLiquidityCell.vue';
import PoolsTableNameCell from './PoolsTableNameCell.vue';

/**
 * TYPES
 */
type Props = {
  data?: PoolNote[];
  poolValueByPoolId: Record<string, number>;
  poolsType?: 'unstaked' | 'staked';
  isLoading?: boolean;
  isLoadingMore?: boolean;
  noPoolsLabel?: string;
  isPaginated?: boolean;
  sortColumn?: string;
  hiddenColumns?: string[];
  skeletonClass?: string;
};

/**
 * PROPS & EMITS
 */

const props = withDefaults(defineProps<Props>(), {
  poolsType: 'unstaked',
  isLoadingMore: false,
  noPoolsLabel: 'No pools',
  isPaginated: false,
  // sortColumn: 'totalLiquidity',
  hiddenColumns: () => [],
  data: () => [],
  skeletonClass: 'h-64',
});

const emit = defineEmits<{
  (e: 'loadMore'): void;
  (e: 'triggerStake', value: Pool): void;
  (e: 'triggerUnstake', value: Pool): void;
  (e: 'onColumnSort', value: string): void;
}>();
/**
 * COMPOSABLES
 */
const router = useRouter();
const { t } = useI18n();
const { trackGoal, Goals } = useFathom();
const { darkMode } = useDarkMode();
const { upToLargeBreakpoint, upToSmallBreakpoint } = useBreakpoints();
const { networkSlug } = useNetwork();

const wideCompositionWidth = computed(() => {
  if (upToSmallBreakpoint.value) return 250;
  return 350;
});

/**
 * DATA
 */
const columns = computed<ColumnDefinition[]>(() => [
  {
    name: 'Icons',
    id: 'icons',
    accessor: 'uri',
    Header: 'iconColumnHeader',
    Cell: 'iconColumnCell',
    width: 125,
    noGrow: true,
  },
  {
    name: t('poolName'),
    id: 'poolName',
    accessor: 'poolName',
    Cell: 'poolNameCell',
    width: props.hiddenColumns.length >= 2 ? wideCompositionWidth.value : 350,
  },
  {
    name: t('capacity', [t('hourAbbrev')]),
    accessor: pool => pool?.capacity || '0',
    align: 'right',
    id: 'capacity',
    Cell: 'capacityCell',
    sortKey: pool => {
      const volume = Number(pool?.capacity);
      if (volume === Infinity || isNaN(volume)) return 0;
      return volume;
    },
    width: 200,
    cellClassName: 'font-numeric',
  },
  {
    name: 'Target Return',
    Cell: 'aprCell',
    align: 'right',
    id: 'apr',
    width: 250,
  },
  {
    name: 'Liquidity',
    id: 'totalLiquidity',
    accessor: 'totalLiquidity',
    align: 'right',
    Cell: 'liquidityCell',
    width: 150,
  },
  {
    name: 'Created At',
    id: 'createdAt',
    accessor: 'createdAt',
    hidden: true,
    sortKey: pool => {
      const unix = Number(pool?.createdAt);
      if (unix === Infinity || isNaN(unix)) return 0;
      return unix;
    },
  },
]);

const visibleColumns = computed(() =>
  columns.value.filter(column => !props.hiddenColumns.includes(column.id))
);

/**
 * METHODS
 */
function handleRowClick(pool: Pool, inNewTab?: boolean) {
  trackGoal(Goals.ClickPoolsTableRow);

  console.log({
    pool,
  });

  const route = router.resolve({
    name:
      pool.id == networkConfig.vaultToken.poolId.toString()
        ? 'vault'
        : 'pool-note',
    params: { id: pool.id, networkSlug },
  });
  inNewTab ? window.open(route.href) : router.push(route);
}

function iconAddresses(poolNote: PoolNote) {
  const isHasSOT = !!poolNote.targetAmount;
  const isHasJOT = !!poolNote.juniorTargetAmount;

  return [isHasJOT && Jot, isHasSOT && Sot].filter(Boolean);
}
</script>

<template>
  <BalCard
    shadow="lg"
    :square="upToLargeBreakpoint"
    :noBorder="upToLargeBreakpoint"
    noPad
  >
    <BalTable
      :columns="visibleColumns"
      :data="data"
      :noResultsLabel="noPoolsLabel"
      :isLoading="isLoading"
      :isLoadingMore="isLoadingMore"
      :skeletonClass="skeletonClass"
      sticky="both"
      :square="upToLargeBreakpoint"
      :onRowClick="handleRowClick"
      :isPaginated="isPaginated"
      isOnlyDescSort
      :initialState="{
        sortColumn: sortColumn as any,
        sortDirection: 'desc',
      }"
      @on-column-sort="emit('onColumnSort', $event)"
      @load-more="emit('loadMore')"
    >
      <template #iconColumnHeader>
        <div class="flex items-center">
          <img
            v-if="darkMode"
            :src="TokensWhite"
            alt="token"
            loading="lazy"
            width="24"
            height="15"
          />
          <img
            v-else
            :src="TokensBlack"
            alt="token"
            loading="lazy"
            width="24"
            height="15"
          />
        </div>
      </template>
      <template #iconColumnCell="poolNote">
        <div v-if="!isLoading" class="py-4 px-6" :data-testid="poolNote?.id">
          <BalAssetSet
            :logoURIs="iconAddresses(poolNote)"
            :balAssetProps="{ imgClass: '!bg-transparent' }"
            :width="100"
          />
        </div>
      </template>
      <template #poolNameCell="poolNote">
        <PoolsTableNameCell :poolNote="poolNote" />
      </template>
      <template #capacityCell="poolNote">
        <PoolsTableCapacityCell :poolNote="poolNote" />
      </template>
      <template #aprCell="poolNote">
        <PoolsTableTargetReturnCell :poolNote="poolNote" />
      </template>
      <template #liquidityCell="poolNote">
        <PoolsTableLiquidityCell :poolNote="poolNote" />
      </template>
    </BalTable>
  </BalCard>
</template>
